<template>
  <div
    class="fixed inset-x-0 top-0 z-50 flex items-start px-5 pointer-events-none"
    :style="{ paddingTop: 'calc(env(safe-area-inset-top) + 6px)' }"
  >
    <BackButton
      v-if="backMode && !isActive"
      class="pointer-events-auto"
      @click="handleBack"
    />

    <button
      v-else
      type="button"
      class="pointer-events-auto relative flex h-[44px] shrink-0 items-center rounded-full bg-[#fbfbfb]/70 p-[3px] backdrop-blur-xl transition-all duration-150 ease-out"
      :class="isActive ? '' : 'pr-2.5'"
      @click="handleLeftClick"
    >
      <span aria-hidden="true" class="shadow--glass-pill"><i></i></span>
      <span class="relative block size-[38px] shrink-0">
        <Avatar
          :size="38"
          :src="user?.avatarUrl"
          class="absolute inset-0 transition-all duration-150 ease-out"
          :class="isActive ? 'scale-50 opacity-0' : 'scale-100 opacity-100'"
        />
        <span
          class="absolute inset-0 flex items-center justify-center transition-all duration-200 ease-[cubic-bezier(0.34,1.56,0.64,1)]"
          :class="isActive ? 'scale-100 opacity-100' : 'scale-50 opacity-0'"
        >
          <component
            :is="isSubView ? ChevronLeftIcon : HomeIcon"
            class="size-[24px] text-gray-800"
          />
        </span>
      </span>
      <span
        class="overflow-hidden whitespace-nowrap text-[15px] font-medium leading-[23px] text-gray-700 transition-all duration-150 ease-out"
        :class="
          isActive ? 'ml-0 max-w-0 opacity-0' : 'ml-2 max-w-[120px] opacity-100'
        "
      >
        {{ firstName }}
      </span>
    </button>

    <div
      class="transition-all duration-150 ease-out"
      :style="{ flexGrow: isActive ? 0 : 1, flexBasis: '0px' }"
    />

    <div
      class="pointer-events-auto relative ml-2.5 flex h-[44px] min-w-0 items-center gap-1.5 rounded-full bg-[#fbfbfb]/70 px-[11px] backdrop-blur-xl transition-all duration-150 ease-out"
      :style="{ flexGrow: isActive ? 1 : 0 }"
      @click="handleFieldClick"
    >
      <span aria-hidden="true" class="shadow--glass-pill"><i></i></span>
      <DacrMarkIcon class="size-[22px] shrink-0 text-gray-800" />
      <input
        v-if="isActive"
        ref="input"
        v-model="mobileSearchStore.query"
        type="text"
        placeholder="Search Dacr"
        class="min-w-0 flex-1 bg-transparent text-[15px] leading-[23px] text-gray-800 outline-none placeholder:text-gray-300"
      />
      <span
        v-else
        class="whitespace-nowrap text-[15px] font-medium leading-[23px] text-gray-300"
      >
        Search
      </span>
      <button
        v-if="isActive && mobileSearchStore.query"
        type="button"
        class="flex size-5 shrink-0 items-center justify-center rounded-full bg-[#8c8c8c]"
        @click.stop="handleClear"
      >
        <CloseIcon class="size-3 text-white" />
      </button>
    </div>

    <div
      class="shrink-0"
      :class="
        isActive ? 'ml-2.5 w-[44px] overflow-visible' : 'w-0 overflow-hidden'
      "
    >
      <button
        type="button"
        class="pointer-events-auto relative flex size-[44px] origin-center items-center justify-center rounded-full bg-[#fbfbfb]/70 backdrop-blur-xl transition-all duration-200 ease-[cubic-bezier(0.34,1.56,0.64,1)]"
        :class="
          isActive
            ? 'scale-100 opacity-100'
            : 'scale-0 opacity-0 pointer-events-none'
        "
        @click="mobileSearchStore.close"
      >
        <span aria-hidden="true" class="shadow--glass-pill"><i></i></span>
        <CloseIcon class="size-[24px] text-gray-800" />
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import type { RouteLocationRaw } from 'vue-router'
import { Avatar } from '@dacr/library'
import { DacrMarkIcon, HomeIcon, CloseIcon, ChevronLeftIcon } from '@dacr/icons'
import BackButton from '@/shared/components/BackButton.vue'
import { useUserStore } from '@/shared/stores/user'
import { useMobileSearchStore } from '@/features/search/store'
import { backOrFallback } from '@/shared/utils/navigation'

const emit = defineEmits<{
  account: []
}>()

const route = useRoute()
const router = useRouter()
const userStore = useUserStore()
const mobileSearchStore = useMobileSearchStore()

const input = ref<HTMLInputElement>()

const user = computed(() => userStore.user)
const firstName = computed(() => user.value?.name?.split(' ')[0] ?? '')
const isActive = computed(() => mobileSearchStore.isActive)
const isSubView = computed(
  () => isActive.value && mobileSearchStore.view !== 'combined'
)
const backMode = computed(
  () =>
    route.name === 'InfringementGroupView' ||
    route.name === 'FileExplorerFolderView' ||
    route.name === 'CopyrightView' ||
    route.name === 'InfringementReportView'
)

const backFallback = computed<RouteLocationRaw>(() => {
  if (route.name === 'FileExplorerFolderView') {
    const path = route.params.path
    const segments = Array.isArray(path) ? path : [path]
    return segments.length > 1
      ? {
          name: 'FileExplorerFolderView',
          params: { path: segments.slice(0, -1) },
        }
      : '/home'
  }
  if (
    route.name === 'InfringementGroupView' ||
    route.name === 'InfringementReportView'
  ) {
    return '/infringement'
  }
  return '/home'
})

const handleBack = () => {
  backOrFallback(router, backFallback.value)
}

watch(isActive, (active) => {
  if (active) nextTick(() => input.value?.focus())
})

const handleLeftClick = () => {
  if (isActive.value) {
    if (mobileSearchStore.view !== 'combined') {
      mobileSearchStore.setView('combined')
    } else {
      mobileSearchStore.close()
      router.push('/home')
    }
  } else {
    emit('account')
  }
}

const handleClear = () => {
  mobileSearchStore.query = ''
  mobileSearchStore.setView('combined')
  input.value?.focus()
}

const handleFieldClick = () => {
  if (isActive.value) {
    input.value?.focus()
  } else {
    mobileSearchStore.showSearch()
  }
}
</script>
